import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputAdornment,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  activitiesConfigToViewModel,
  ImplementActivitiesFormViewModel,
  viewModelToActivitiesConfig,
} from "../../../../components/atk/ImplementActivitiesFormViewModel";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { AutonomousActivitySwitcher } from "../../../../components/atk/AutonomousActivitySwitcher";
import {
  AutonomousActivityType,
  ScvAutonomyAuxControlAction,
} from "protos/atk/enums";
import { CarbonLoadingSpinner } from "../../../../components/CarbonLoadingSpinner";
import { Field, Formik, FormikConfig, useFormikContext } from "formik";
import { Select as FormikSelect, Select, TextField } from "formik-mui";
import { friendlyScvInputName } from "portal/utils/atkEquipment";
import { GetRigResponse } from "protos/portal/atk_equipment";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { ScvAuxControlSelect } from "../../../../components/atk/ScvAuxControlSelect";
import { TFunction } from "i18next";
import { typedPath } from "typed-path";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useCallback, useState } from "react";

interface ActivitiesConfigDialogFormState
  extends ImplementActivitiesFormViewModel {}

const S = typedPath<ActivitiesConfigDialogFormState>();

function friendlyNameForScvChannel(
  t: TFunction,
  getRigResult: GetRigResponse | undefined,
  channelName: "height" | "aux1" | "aux2"
): string | undefined {
  const scvAutonomy = getRigResult?.rig?.controls?.scvAutonomy;
  const effectiveConfig = getRigResult?.validationResult?.effectiveConfig;

  if (scvAutonomy && effectiveConfig) {
    if (scvAutonomy[`${channelName}ScvConnectionId`]) {
      const scv = effectiveConfig.scvs.find(
        (scv) =>
          scv.scvConnectionId === scvAutonomy[`${channelName}ScvConnectionId`]
      );

      if (scv) {
        const implementId = scv.implementId;
        const implement = effectiveConfig.implements.find(
          (impl) => impl.implementId === implementId
        );

        if (implement && scv.input) {
          return friendlyScvInputName(t, implement, scv.input);
        }
      }
    } else {
      // heightScvConnectionId is absent; no SCV is controlled by this control
      // channel.
      return t("utils.descriptors.none");
    }
  }
}

const _ActivitiesConfigModal: React.FC<{
  jobId: number;
  onClose: () => void;
}> = ({ jobId, onClose }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched, validateForm, submitForm } =
    useFormikContext<ActivitiesConfigDialogFormState>();
  const [viewingActivity, setViewingActivity] =
    useState<AutonomousActivityType>(
      AutonomousActivityType.ACTIVITY_WORKING_ROW
    );
  const { data: configuredJob, isLoading: isJobLoading } = useQueryPopups(
    rtcJobsApi.endpoints.getConfiguredJob.useQuery(
      { id: jobId.toString() },
      { skip: !jobId }
    )
  );
  const { data: getRigResult, isLoading: isRigLoading } = useQueryPopups(
    atkEquipmentApi.endpoints.getRig.useQuery(
      {
        id: configuredJob?.groundPrep?.rigId ?? "",
        validate: true,
      },
      { skip: !configuredJob?.groundPrep?.rigId }
    )
  );

  const heightScvName: string | undefined = friendlyNameForScvChannel(
    t,
    getRigResult,
    "height"
  );
  const aux1ScvName: string | undefined = friendlyNameForScvChannel(
    t,
    getRigResult,
    "aux1"
  );
  const aux2ScvName: string | undefined = friendlyNameForScvChannel(
    t,
    getRigResult,
    "aux2"
  );

  return (
    <>
      <DialogContent className="flex flex-col p-0 py-4">
        {!isJobLoading && !isRigLoading ? (
          <>
            <div className="flex flex-col mx-4 gap-4 mb-4">
              <Typography>
                {t(
                  "views.autotractor.autonomousActivities.activitySelectionLabel"
                )}
              </Typography>
              <AutonomousActivitySwitcher
                variant="with-descriptions"
                activeActivity={viewingActivity}
                setActiveActivity={setViewingActivity}
              />
            </div>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t(
                  "views.autotractor.rigDetails.configuration.threePointAutonomy.sectionHeader"
                )}
              </AccordionSummary>
              <AccordionDetails className="grid grid-cols-2 gap-4">
                <FormControl fullWidth>
                  <Typography variant="body1" id="entry-label">
                    {t(
                      "views.autotractor.autonomousActivities.momentaryControlMode.header"
                    )}
                  </Typography>
                  <Field
                    component={Select}
                    name={
                      S.activities[viewingActivity].rearThreePtControl
                        .entryActivity.$path
                    }
                    aria-labelledby="entry-label"
                  >
                    <MenuItem value="go-to-height">
                      {t(
                        "views.autotractor.autonomousActivities.momentaryControlMode.goToPosition"
                      )}
                    </MenuItem>
                    <MenuItem value="detent-up">
                      {t(
                        "views.autotractor.autonomousActivities.auxControlAction.detentUp"
                      )}
                    </MenuItem>
                    <MenuItem value="detent-down">
                      {t(
                        "views.autotractor.autonomousActivities.auxControlAction.detentDown"
                      )}
                    </MenuItem>
                    <MenuItem value="none">
                      {t(
                        "views.autotractor.autonomousActivities.auxControlAction.none"
                      )}
                    </MenuItem>
                  </Field>
                </FormControl>
                <FormControl fullWidth>
                  <Typography id="entry-target-height-label">
                    {t(
                      "views.autotractor.autonomousActivities.heightControlTargetPositionLabel"
                    )}
                  </Typography>
                  <Field
                    component={TextField}
                    name={
                      S.activities[viewingActivity].rearThreePtControl
                        .entryGoToHeightPct.$path
                    }
                    disabled={
                      values.activities[viewingActivity]?.rearThreePtControl
                        .entryActivity !== "go-to-height"
                    }
                    aria-labelledby="entry-target-height-label"
                    className="mt-2"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {t("utils.units.%")}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <Typography variant="body1" id="hold-label">
                    {t(
                      "views.autotractor.autonomousActivities.holdControlMode.header"
                    )}
                  </Typography>
                  <Field
                    component={Select}
                    name={
                      S.activities[viewingActivity].rearThreePtControl
                        .holdActivity.$path
                    }
                    aria-labelledby="hold-label"
                  >
                    <MenuItem value="hold-height">
                      {t(
                        "views.autotractor.autonomousActivities.heightControlMode.holdHeight"
                      )}
                    </MenuItem>
                    <MenuItem value="hold-float">
                      {t(
                        "views.autotractor.autonomousActivities.auxControlAction.float"
                      )}
                    </MenuItem>
                    <MenuItem value="none">
                      {t(
                        "views.autotractor.autonomousActivities.auxControlAction.none"
                      )}
                    </MenuItem>
                  </Field>
                </FormControl>
                <FormControl fullWidth>
                  <Typography id="hold-target-height-label">
                    {t(
                      "views.autotractor.autonomousActivities.heightControlTargetPositionLabel"
                    )}
                  </Typography>
                  <Field
                    component={TextField}
                    name={
                      S.activities[viewingActivity].rearThreePtControl
                        .holdHeightPct.$path
                    }
                    disabled={
                      values.activities[viewingActivity]?.rearThreePtControl
                        .holdActivity !== "hold-height"
                    }
                    aria-labelledby="hold-target-height-label"
                    className="mt-2"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {t("utils.units.%")}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t(
                  "views.autotractor.rigDetails.configuration.scvAutonomy.sectionHeader"
                )}
              </AccordionSummary>
              <AccordionDetails className="grid grid-cols-2 gap-4">
                <div className="col-span-full">
                  <Typography variant="body1">
                    {t(
                      "views.autotractor.rigDetails.configuration.scvAutonomy.heightScv"
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {heightScvName}
                  </Typography>
                </div>
                <FormControl fullWidth>
                  <div id="height-control-label">
                    <Typography id="height-control-mode-label">
                      {t(
                        "views.autotractor.autonomousActivities.heightControlModeLabel"
                      )}
                    </Typography>
                  </div>
                  <Field
                    component={FormikSelect}
                    name={S.activities[viewingActivity].heightActivity.$path}
                    aria-labelledby="height-control-mode-label"
                  >
                    <MenuItem value="none">
                      {t(
                        "views.autotractor.autonomousActivities.heightControlMode.none"
                      )}
                    </MenuItem>
                    <MenuItem value="scv-off">
                      {t(
                        "views.autotractor.autonomousActivities.heightControlMode.scvOff"
                      )}
                    </MenuItem>
                    <MenuItem value="hold-height">
                      {t(
                        "views.autotractor.autonomousActivities.heightControlMode.holdHeight"
                      )}
                    </MenuItem>
                  </Field>
                </FormControl>
                <FormControl fullWidth>
                  <Typography id="target-height-label">
                    {t(
                      "views.autotractor.autonomousActivities.heightControlTargetPositionLabel"
                    )}
                  </Typography>
                  <Field
                    component={TextField}
                    name={S.activities[viewingActivity].heightPct.$path}
                    disabled={
                      values.activities[viewingActivity]?.heightActivity !==
                      "hold-height"
                    }
                    aria-labelledby="target-height-label"
                    className="mt-2"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {t("utils.units.%")}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>

                <hr className="w-full col-span-full" />

                <div className="col-span-full">
                  <Typography variant="body1">
                    {t(
                      "views.autotractor.rigDetails.configuration.scvAutonomy.aux1Scv"
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {aux1ScvName}
                  </Typography>
                </div>
                <FormControl fullWidth className="col-span-full mt-2">
                  <Typography variant="body1" id="aux1-label">
                    {t(
                      "views.autotractor.autonomousActivities.aux1EntryActionLabel"
                    )}
                  </Typography>
                  <ScvAuxControlSelect
                    aria-labelledby="aux1-label"
                    value={
                      values.activities[viewingActivity]?.aux1Action ??
                      ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE
                    }
                    onChange={(e) => {
                      const name =
                        S.activities[viewingActivity].aux1Action.$path;

                      // work around https://github.com/jaredpalmer/formik/issues/1191
                      // and https://github.com/jaredpalmer/formik/issues/2083
                      setFieldValue(name, e.target.value).finally(() =>
                        setFieldTouched(name, true)
                      );
                      validateForm();
                    }}
                  />
                </FormControl>

                <hr className="w-full col-span-full" />

                <div className="col-span-full">
                  <Typography variant="body1">
                    {t(
                      "views.autotractor.rigDetails.configuration.scvAutonomy.aux2Scv"
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {aux2ScvName}
                  </Typography>
                </div>
                <FormControl fullWidth className="col-span-full mt-2">
                  <Typography variant="body1" id="aux2-label">
                    {t(
                      "views.autotractor.autonomousActivities.aux2EntryActionLabel"
                    )}
                  </Typography>
                  <ScvAuxControlSelect
                    aria-labelledby="aux2-label"
                    value={
                      values.activities[viewingActivity]?.aux2Action ??
                      ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE
                    }
                    onChange={(e) => {
                      const name =
                        S.activities[viewingActivity].aux2Action.$path;

                      // work around https://github.com/jaredpalmer/formik/issues/1191
                      // and https://github.com/jaredpalmer/formik/issues/2083
                      setFieldValue(name, e.target.value).finally(() =>
                        setFieldTouched(name, true)
                      );
                      validateForm();
                    }}
                  />
                </FormControl>
              </AccordionDetails>
            </Accordion>
          </>
        ) : (
          <CarbonLoadingSpinner />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onClose}>
          {t("utils.actions.cancel")}
        </Button>
        <Button variant="contained" color="primary" onClick={submitForm}>
          {t("utils.actions.save")}
        </Button>
      </DialogActions>
    </>
  );
};

export const ActivitiesConfigModal: React.FC<{
  open: boolean;
  onClose: () => void;
  jobId: number;
}> = ({ open, onClose, jobId }) => {
  const { data: currentActivitiesResult, isLoading } = useQueryPopups(
    rtcJobsApi.endpoints.getScvActivitiesConfiguration.useQuery({
      jobId: jobId.toString(),
    })
  );
  const [updateActivities] = useMutationPopups(
    rtcJobsApi.endpoints.setScvActivitiesConfiguration.useMutation()
  );

  const onSubmit: FormikConfig<ActivitiesConfigDialogFormState>["onSubmit"] =
    useCallback(
      async (values) => {
        const result = await updateActivities({
          jobId: jobId.toString(),
          config: viewModelToActivitiesConfig(values),
        });

        if (!result.error) {
          onClose();
        }
      },
      [jobId, onClose, updateActivities]
    );

  return (
    <Dialog open={open} onClose={onClose}>
      {isLoading ? (
        <CarbonLoadingSpinner />
      ) : (
        <Formik
          initialValues={activitiesConfigToViewModel(currentActivitiesResult)}
          onSubmit={onSubmit}
          enableReinitialize
        >
          <_ActivitiesConfigModal jobId={jobId} onClose={onClose} />
        </Formik>
      )}
    </Dialog>
  );
};
